import React, {
  forwardRef, useContext, useMemo, useEffect, useRef, useState, lazy, Suspense
} from 'react';
import proptypes from 'prop-types';
import { extend, QueryContext } from '@thd-nucleus/data-sources';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import { SponsoredInGridBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { CategoryCardsDesktop, CategoryCardsMobile } from '@thd-olt-component-react/category-cards';
import { DiscoveryZonesWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { createGridWithPod, ProductResultsContext } from '@thd-olt-component-react/product-results';
import { DynamicComponent } from '@thd-nucleus/app-render';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { CustomProductPodResponsive } from './CustomProductPodResponsive';
import { CustomProductPianoPodMobile } from './CustomProductPianoPodMobile';
import { CustomProductKeylessPodMobile } from './CustomProductKeylessPodMobile';
import { CustomProductPodThreeColumn } from './CustomProductPodThreeColumn';
import { CustomProductPodResponsiveB2B } from './CustomProductPodResponsiveB2B';
import { CustomProductPodTwoByTwoColumn } from './CustomProductPodTwoByTwoColumn';
import {
  CustomProductPodResponsive as CustomProductPodResponsiveMA
} from './major-appliances/CustomProductPodResponsive';
import {
  CustomProductPianoPodMobile as CustomProductPianoPodMobileMA
} from './major-appliances/CustomProductPianoPodMobile';
import { CustomProductKeylessPodMobileB2B } from './CustomProductKeylessPodMobileB2B';
import { CustomProductPianoPodMobileB2B } from './CustomProductPianoPodMobileB2B';
import {
  CustomProductPianoPodKeysMobile as CustomProductPianoPodKeysMobileMA
} from './major-appliances/CustomProductPianoPodKeysMobile';
import {
  AI_ASSISTED_SEARCH_BANNER, getInGridPlacementSlots, IN_GRID_COMPONENT_REGISTRY
} from '../in-grid-placements';
import { isRefrigeratorPlp } from '../util';
import { usePlpGridHealthTracker } from '../hooks/usePlpGridHealthTracker';

const DEFAULT_LAYOUT_STYLE = 'Grid';
const MAJOR_APPLIANCES_EXP = 'ma';
const APPLIANCE_MATCHMAKER_MIN_HEIGHT = '176px';

const ApplianceMatchmaker = lazy(() => import('@thd-olt-component-react/appliance-matchmaker')
  .then((module) => ({ default: module.ApplianceMatchmaker })));

const {
  string, func, bool, number, arrayOf, shape
} = proptypes;

const DefaultResultsGrid = createGridWithPod(CustomProductPodResponsive);

const DefaultGrid = forwardRef(({
  pageType,
  defaultVariables,
  handleProductClick,
  productPodHoverDelay,
  onCompare,
  onSelectedForCompare,
  selectedForCompare,
  isInStoreFilterON,
  samplesLoading,
  hasSameDayFilter,
  impProductPodData,
  nValue,
  additionalSearchParams,
  keyword,
  storeId,
  itemId,
  productSSR,
  searchSSR,
  ssrProductRequestCount,
  dynamicHydrationProductRequestCount,
  isInGridBannerBottom,
  searchModelData = {},
  searchModelLoading = false,
  searchModelError = null,
  pageSize,
  experienceType,
  inGridPlacements = []
}, refFromParent) => {
  const internalRef = useRef(null);
  const ref = refFromParent || internalRef;
  const {
    data: prodResultsData, loading: prodResultsLoading, error: prodResultsError
  } = useContext(ProductResultsContext);
  let data = {};
  let error = null;
  let loading = false;
  if (prodResultsData && Object.keys(prodResultsData).length > 0) {
    data = prodResultsData;
    error = prodResultsError;
  } else if (searchModelData && Object.keys(searchModelData).length > 0) {
    data = searchModelData;
    error = searchModelError;
  }

  loading = (prodResultsData && Object.keys(prodResultsData).length > 0)
    ? prodResultsLoading
    : searchModelLoading;

  const { channel, customer, isConsumerApp } = useContext(ExperienceContext);
  const isB2B = customer?.type === 'b2b';

  const matchesScreen = useBreakpoint('sm', { current: true });
  const isDesktop = channel === 'desktop';
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const isPep = pageType === 'pep';
  const isMA = experienceType === MAJOR_APPLIANCES_EXP;
  const isMobileOrConsumerApp = isConsumerApp || channel === 'mobile';
  const thdCustomer = useThdCustomer();
  const { mcvisID } = thdCustomer || {};

  const enableApplianceMatchmaker = useConfigService('fs:enableApplianceMatchmaker');
  const enablePlpGridTracker = useConfigService('fs:enablePlpGridTracker');
  const enablePianoPodKeys = useConfigService('fs-prop:enablePianoPodKeys');
  const isPianoPodEnabled = useConfigService('fs:isPianoPodEnabled');
  const sponsoredCarouselConfig = useConfigService('fs-prop:tnt-carouselType');
  const isInGridBannerEnabled = useConfigService('fs-prop:isInGridBannerEnabled') || false;
  const abTestAssistedSearchBannerRow = useConfigService('fs-ai-assisted-search-banner')?.row;
  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  // Feature Switch that enables hiding or showing pickup and delivery messaging based on filter selection.
  const isFulfillmentFSEnabled = useConfigService('fs:isFulfillmentFilterAndMsgEnabled');
  const enablePlccPlpComponent = useConfigService('fs:enablePlccPlpComponent');
  const fsisViewSimilarButtonPositionAboveABTest = useConfigService('fs:isViewSimilarButtonPositionAboveABTest')
      || false;
  const fsisViewSimilarButtonPositionBelowABTest = useConfigService('fs:isViewSimilarButtonPositionBelowABTest')
      || false;

  // Combine these flags into a single condition we can check more easily.
  const isDiscoveryZonesEligible = (isBrowse && isDiscoveryZonesEnabled) || (isSearch && isDiscoveryZonesSearchEnabled);
  const { isClientResolved } = useContext(QueryContext);
  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });
  const showHideViewSimilarIcon = (
    fsisViewSimilarButtonPositionAboveABTest
      || fsisViewSimilarButtonPositionBelowABTest
  );
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const { isLocalized } = store || {};
  const [layoutStyle, setLayoutStyle] = useState(DEFAULT_LAYOUT_STYLE);
  const isLayoutStyleList = layoutStyle === 'List';

  // Trigger EVENT_BUS to notify PLPResultsHeader about the ref
  useEffect(() => {
    if (ref && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      if (ref.current) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('default-grid.results-ref-update', {
          ref: ref.current
        });
      }
    }
  }, [ref]);

  // Feature switch that enables New Relic tracking of empty PLP grids.
  usePlpGridHealthTracker({
    enabled: !!enablePlpGridTracker,
    store,
    pageType,
    channel,
    experienceType,
    customerType: customer?.type,
    isConsumerApp,
    resultCount: data?.searchModel ? (data.searchModel.products?.length ?? null) : null,
    resultsLoading: loading
  });

  // Listen to PLP_LAYOUTSTYLE.value event to change the type of CustomProductPod to display
  useEffect(() => {
    if (
      typeof window === 'undefined'
      || !window.LIFE_CYCLE_EVENT_BUS
      || typeof window.LIFE_CYCLE_EVENT_BUS.on !== 'function'
    ) {
      return undefined;
    }
    const layoutStyleHandler = ({ output } = {}) => {
      const layoutStyleValue = (output?.layoutStyle) || DEFAULT_LAYOUT_STYLE;
      setLayoutStyle(layoutStyleValue);
    };

    window.LIFE_CYCLE_EVENT_BUS.on('PLP_LAYOUTSTYLE.value', layoutStyleHandler);

    return () => {
      if (typeof window !== 'undefined'
        && window.LIFE_CYCLE_EVENT_BUS
        && typeof window.LIFE_CYCLE_EVENT_BUS.off === 'function') {
        window.LIFE_CYCLE_EVENT_BUS.off('PLP_LAYOUTSTYLE.value', layoutStyleHandler);
      }
    };
  }, []);

  const shouldShowSponsoredCarousel = useMemo(() => {
    return (sponsoredCarouselConfig === 'standardDZ'
      || sponsoredCarouselConfig === 'longtailDZ'
      || sponsoredCarouselConfig === 'fourStarDZ');
  }, [sponsoredCarouselConfig]);
  const shouldShowDiscoveryZones = (isBrowse && !shouldShowSponsoredCarousel) || (isSearch && isDiscoveryZonesEligible);
  const shouldShowRetailMediaCarousel = isBrowse && shouldShowSponsoredCarousel;
  // this is needed for sponsored in grid
  const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray.length > 0;
  const { metadata } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';
  const SERVER_SIDE_PRODUCT_POD_COUNT = isSearch ? 24 : 12;

  const { products = [] } = data?.searchModel || {};
  const CategoryCardsComponent = isDesktop ? CategoryCardsDesktop : CategoryCardsMobile;

  // Setup for the appliance-matchmaker entry point: only refrigerator PLPs should show it.
  const hasLoadedProducts = !loading && products.length > 0;
  const [isRefrigeratorPlpResult, setIsRefrigeratorPlpResult] = useState(false);
  useEffect(() => {
    if (hasLoadedProducts) {
      setIsRefrigeratorPlpResult(isRefrigeratorPlp(products));
    }
  }, [hasLoadedProducts, products]);
  const shouldShowApplianceMatchmaker = isMA && isBrowse && channel === 'mobile'
    && isRefrigeratorPlpResult && enableApplianceMatchmaker;

  const itemIds = useMemo(() => {
    return products.map((product) => product.itemId);
  }, [products]);

  const DiscoveryZoneContainer = isLayoutStyleList ? 'div' : Row;
  const discoveryZoneHydratorClassName = isLayoutStyleList
    ? 'zone-card sui-grid'
    : 'sui-bg-primary sui-grid sui-m-1 sui-w-full';
  const slots = useMemo(() => {
    if (isPep) {
      return [];
    }

    const slotsList = [
      {
        position: {
          xl: 3, lg: 3, md: 6, sm: 6, unprefixed: 6
        },
        component: (
          <DynamicComponent pageType={pageType}>
            <Hydrator
              id="category-cards-slot"
              className="sui-px-1"
            >
              <ErrorBoundary>
                <CategoryCardsComponent
                  data={data}
                  error={error}
                  loading={loading}
                  storeId={storeId}
                  keyword={keyword}
                  navParam={nValue}
                  noMargin
                />
              </ErrorBoundary>
            </Hydrator>
          </DynamicComponent>
        )
      }
    ];
    // add DZ
    if (isDiscoveryZonesEligible) {
      slotsList.push({
        position: {
          xl: 2, lg: 2, md: 2, sm: 2, unprefixed: 4
        },
        component: (
          <DiscoveryZoneContainer key="discovery-zone-1" data-testid="discovery-zone-1">
            <DiscoveryZonesWrapper
              data={data}
              loading={loading}
              nValue={nValue}
              keyword={keyword}
              schemaName="discoveryZones"
              storeId={storeId}
              zoneId="1"
              errorBoundary
              pageType={pageType}
              itemIds={itemIds}
              hydrator={{
                className: discoveryZoneHydratorClassName,
                delay: 1500,
                scrollBuffer: 500,
                id: 'discovery-zones-1'
              }}
              dynamic={{
                pageType
              }}
            />
          </DiscoveryZoneContainer>
        )
      });
    }

    if (shouldShowDiscoveryZones) {
      slotsList.push(
        {
          position: {
            xl: 4, lg: 4, md: 4, sm: 4, unprefixed: 8
          },
          component: (
            <DiscoveryZoneContainer key="discovery-zone-2" data-testid="discovery-zone-2">
              <DiscoveryZonesWrapper
                data={data}
                loading={loading}
                nValue={nValue}
                keyword={keyword}
                schemaName="discoveryZones"
                storeId={storeId}
                zoneId="2"
                errorBoundary
                pageType={pageType}
                itemIds={itemIds}
                hydrator={{
                  className: discoveryZoneHydratorClassName,
                  delay: 1500,
                  scrollBuffer: 500,
                  id: 'discovery-zones-2'
                }}
                dynamic={{
                  pageType
                }}
              />
            </DiscoveryZoneContainer>
          )
        });
    }
    // add sponsored carousel
    if (shouldShowRetailMediaCarousel) {
      slotsList.push(
        {
          position: {
            xl: 4, lg: 4, md: 4, sm: 4, unprefixed: 8
          },
          component: (
            <Row className="grid sui-pb-4 sui-pl-2" data-testid="SponsoredCarouselWrapper">
              <RetailMediaCarousel
                pageContext={{
                  schema: 'discovery_zone',
                  isDiscoveryZone: true
                }}
                showSponsoredCarousel
                plaLocation="sponsoredCarouselBrowsePageDiscoveryZone"
                nValue={nValue}
                tntCarouselType={sponsoredCarouselConfig}
                dynamic={{
                  pageType
                }}
              />
            </Row>
          )
        });
    }

    if (isInGridBannerEnabled
      && !isInGridBannerBottom
      && (pageSize == null || pageSize !== 12)
      && isLocalized
      && !loading && data?.searchModel
    ) {
      let position = { unprefixed: 3, sm: 3, md: 3 };
      const productsLength = data?.searchModel?.products.length;
      if (['xl', '2xl'].includes(matchesScreen.current) && hasCategoryCard
        && productsLength >= 24) {
        position = { ...position, xl: 6, lg: 6 };
      } else if (['xl', '2xl'].includes(matchesScreen.current) && !hasCategoryCard
        && productsLength >= 12) {
        position = { ...position, xl: 3, lg: 3 };
      }
      if (matchesScreen.current === 'lg' && productsLength >= 18 && hasCategoryCard) {
        position = { ...position, xl: 6, lg: 6 };
      } else if (matchesScreen.current === 'lg' && productsLength >= 9 && !hasCategoryCard) {
        position = { ...position, xl: 3, lg: 3 };
      }
      slotsList.push(
        {
          position,
          component: (
            <Hydrator id="sponsored-banner-slot" className="sui-px-1">
              <Row
                className="sui-py-1 sui-p-1 empty:sui-py-0"
                data-testid="SponsoredInGridBanner"
              >
                <ErrorBoundary name="sponsored-ingrid-nucleus">
                  <SponsoredInGridBanner
                    browserId={mcvisID || ''}
                    pageContext={{
                      label: 'browse-search',
                      data,
                      keyword,
                      isCategory,
                      isBrowse,
                      isSearch
                    }}
                  />
                </ErrorBoundary>
              </Row>
            </Hydrator>
          )
        });
    }

    if (inGridPlacements?.length) {
      slotsList.push(...getInGridPlacementSlots({
        placements: inGridPlacements,
        placementOverride: { [AI_ASSISTED_SEARCH_BANNER]: abTestAssistedSearchBannerRow },
        registry: IN_GRID_COMPONENT_REGISTRY,
        unprefixedPodsPerRow: isDesktop ? 2 : 1
      }));
    }

    return slotsList;
  }, [isDiscoveryZonesEnabled, isDiscoveryZonesSearchEnabled,
    shouldShowSponsoredCarousel, data, error, loading,
    matchesScreen.current, keyword, nValue, storeId, pageType, mcvisID, isLocalized,
    isLayoutStyleList, inGridPlacements, isDesktop, abTestAssistedSearchBannerRow]);

  const renderPod = (product, index) => {
    const getPodComponent = () => {
      const enableKeylessPodB2B = isMobileOrConsumerApp && isPianoPodEnabled && isB2B;
      const isDesktopOrB2BNonMA = isDesktop || (isB2B && !isMA && !enableKeylessPodB2B);
      const viewSimilarButtonConfig = showHideViewSimilarIcon
        ? {
          isViewSimilarTopButtonEnabled: fsisViewSimilarButtonPositionAboveABTest,
          isViewSimilarBottomButtonEnabled: fsisViewSimilarButtonPositionBelowABTest,
          productArray: products,
        }
        : null;

      // Pod with Keys (Details | Specs) for B2B, B2B MA and B2C MA on mobile.
      // Checked before every other branch so it wins over fs:isPianoPodEnabled.
      // B2C non-MA keeps using the CustomProductPianoPodMobile branch further down.
      const showPodKeys = !isDesktop && isMobileOrConsumerApp && enablePianoPodKeys;

      if (showPodKeys && isB2B) {
        return (
          <CustomProductPianoPodMobileB2B
            itemId={product.itemId}
            parent="plp"
            parentId={product?.identifiers?.parentId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            itemRecsIndex={index}
            hasInStoreFilter={isInStoreFilterON}
            impProductPodData={impProductPodData}
            hasSameDayFilter={hasSameDayFilter}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            minimumOrderQuantity={product?.info?.minimumOrderQuantity || null}
            isMA={isMA}
          />
        );
      }

      if (showPodKeys && isMA) {
        return (
          <CustomProductPianoPodKeysMobileMA
            itemId={product.itemId}
            parentId={product?.identifiers?.parentId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            hasInStoreFilter={isInStoreFilterON}
            impProductPodData={impProductPodData}
            showProjectQuantity
            hasSameDayFilter={hasSameDayFilter}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            isViewSimilarButtonEnabled={viewSimilarButtonConfig}
            minimumOrderQuantity={product?.info?.minimumOrderQuantity}
          />
        );
      }

      if (isDesktopOrB2BNonMA && isLayoutStyleList) {
        if (isMA) {
          return (
            <CustomProductPodTwoByTwoColumn
              itemId={product?.itemId}
              onClick={handleProductClick}
              storeId={storeId}
              position={index}
              onCompare={onCompare}
              onSelectedForCompare={onSelectedForCompare}
              selectedForCompare={selectedForCompare}
              hasInStoreFilter={isInStoreFilterON}
              impProductPodData={impProductPodData}
              isB2BCustomer={isB2B}
              url={product?.identifiers?.canonicalUrl}
              isBundleProduct={product?.bundleFlag || false}
              hasSameDayFilter={hasSameDayFilter}
              isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            />
          );
        }

        return (
          <div className="sui-col-span-4">
            <CustomProductPodThreeColumn
              itemId={product.itemId}
              storeId={storeId}
              position={index}
              onClick={(evt) => handleProductClick({ evt, product })}
              hasInStoreFilter={isInStoreFilterON}
              itemRecsIndex={index}
              samplesLoading={samplesLoading}
              impProductPodData={impProductPodData}
              isB2BCustomer={isB2B}
              hasSameDayFilter={hasSameDayFilter}
              isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            />
          </div>
        );
      }

      if (isDesktopOrB2BNonMA) {
        if (isB2B && !isMA) {
          return (
            <CustomProductPodResponsiveB2B
              itemId={product.itemId}
              url={product?.identifiers?.canonicalUrl}
              storeId={storeId}
              position={index}
              onClick={(evt) => handleProductClick({ evt, product })}
              hasInStoreFilter={isInStoreFilterON}
              samplesLoading={samplesLoading}
              impProductPodData={impProductPodData}
              hasSameDayFilter={hasSameDayFilter}
              parentId={product?.identifiers?.parentId}
              itemRecsIndex={index}
            />
          );
        }

        // MA uses the same pod for both B2B and B2C
        if (isMA) {
          return (
            <CustomProductPodResponsiveMA
              itemId={product.itemId}
              parentId={product?.identifiers?.parentId}
              storeId={storeId}
              position={index}
              onCompare={onCompare}
              onSelectedForCompare={onSelectedForCompare}
              selectedForCompare={selectedForCompare}
              hasInStoreFilter={isInStoreFilterON}
              impProductPodData={impProductPodData}
              showProjectQuantity
              hasSameDayFilter={hasSameDayFilter}
              url={product?.identifiers?.canonicalUrl}
              isB2BCustomer={isB2B}
              isBundleProduct={product?.bundleFlag || false}
              isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
              isViewSimilarButtonEnabled={viewSimilarButtonConfig}
            />
          );
        }

        return (
          <CustomProductPodResponsive
            itemId={product.itemId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            onClick={(evt) => handleProductClick({ evt, product })}
            hasInStoreFilter={isInStoreFilterON}
            samplesLoading={samplesLoading}
            impProductPodData={impProductPodData}
            hasSameDayFilter={hasSameDayFilter}
            isMobileOrConsumerApp={isMobileOrConsumerApp}
          />
        );
      }

      if (enableKeylessPodB2B) {
        return (
          <CustomProductKeylessPodMobileB2B
            itemId={product.itemId}
            parent="plp"
            parentId={product?.identifiers?.parentId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            itemRecsIndex={index}
            onClick={(evt) => handleProductClick({ evt, product })}
            hasInStoreFilter={isInStoreFilterON}
            impProductPodData={impProductPodData}
            hasSameDayFilter={hasSameDayFilter}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            minimumOrderQuantity={product?.info?.minimumOrderQuantity || null}
            isMA={isMA}
          />
        );
      }

      if (isMA && isB2B) {
        return (
          <CustomProductPodResponsiveMA
            itemId={product.itemId}
            parentId={product?.identifiers?.parentId}
            storeId={storeId}
            position={index}
            hasInStoreFilter={isInStoreFilterON}
            impProductPodData={impProductPodData}
            showProjectQuantity
            hasSameDayFilter={hasSameDayFilter}
            url={product?.identifiers?.canonicalUrl}
            isB2BCustomer={isB2B}
            isMobileOrConsumerApp={isMobileOrConsumerApp}
            isBundleProduct={product?.bundleFlag || false}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            isViewSimilarButtonEnabled={viewSimilarButtonConfig}
          />
        );
      }

      if (isMA) {
        return (
          <CustomProductPianoPodMobileMA
            itemId={product.itemId}
            parentId={product?.identifiers?.parentId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            hasInStoreFilter={isInStoreFilterON}
            impProductPodData={impProductPodData}
            showProjectQuantity
            hasSameDayFilter={hasSameDayFilter}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            isViewSimilarButtonEnabled={viewSimilarButtonConfig}
            minimumOrderQuantity={product?.info?.minimumOrderQuantity}
          />
        );
      }

      // Temporary second mobile Pod for testing Piano Pod Keys vs Keyless Pods on mobile.
      // Will be removed in a future release after feature-switch test is complete.
      if (isMobileOrConsumerApp && enablePianoPodKeys) {
        return (
          <CustomProductPianoPodMobile
            itemId={product.itemId}
            parent="plp"
            parentId={product?.identifiers?.parentId}
            url={product?.identifiers?.canonicalUrl}
            storeId={storeId}
            position={index}
            itemRecsIndex={index}
            onClick={(evt) => handleProductClick({ evt, product })}
            onSelectedForCompare={onSelectedForCompare}
            hasInStoreFilter={isInStoreFilterON}
            samplesLoading={samplesLoading}
            impProductPodData={impProductPodData}
            hasSameDayFilter={hasSameDayFilter}
            isMobileOrConsumerApp={isMobileOrConsumerApp}
            isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
            showProjectQuantity
            minimumOrderQuantity={product?.info?.minimumOrderQuantity || null}
            enablePlccPlpComponent={enablePlccPlpComponent}
            showHideViewSimilarIcon={showHideViewSimilarIcon ? {
              products,
              fsisViewSimilarButtonPositionAboveABTest,
              fsisViewSimilarButtonPositionBelowABTest
            } : {}}
          />
        );
      }

      return (
        <CustomProductKeylessPodMobile
          itemId={product.itemId}
          parent="plp"
          parentId={product?.identifiers?.parentId}
          url={product?.identifiers?.canonicalUrl}
          storeId={storeId}
          position={index}
          itemRecsIndex={index}
          onClick={(evt) => handleProductClick({ evt, product })}
          onSelectedForCompare={onSelectedForCompare}
          hasInStoreFilter={isInStoreFilterON}
          samplesLoading={samplesLoading}
          impProductPodData={impProductPodData}
          hasSameDayFilter={hasSameDayFilter}
          isMobileOrConsumerApp={isMobileOrConsumerApp}
          isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
          showProjectQuantity
          minimumOrderQuantity={product?.info?.minimumOrderQuantity?.minimumOrderQuantity || null}
          enablePlccPlpComponent={enablePlccPlpComponent}
        />
      );
    };

    return getPodComponent();
  };

  return (
    <>
      { shouldShowApplianceMatchmaker && (
        <div
          style={{ minHeight: APPLIANCE_MATCHMAKER_MIN_HEIGHT }}
          data-testid="appliance-matchmaker-entry-point-container"
        >
          <ErrorBoundary name="appliance-matchmaker-entry-point">
            <Suspense fallback={null}>
              <ApplianceMatchmaker />
            </Suspense>
          </ErrorBoundary>
        </div>
      )}
      <DefaultResultsGrid
        additionalSearchParams={additionalSearchParams}
        keyword={keyword}
        storeId={storeId}
        itemId={itemId}
        productSSR={productSSR}
        searchSSR={searchSSR}
        pageType={pageType}
        ref={ref}
        defaultVariables={defaultVariables}
        slots={slots}
        podFunction={renderPod}
        useTwoColumnPods={isMA && isLayoutStyleList}
        ssrProductRequestCount={ssrProductRequestCount}
        dynamicHydrationProductRequestCount={dynamicHydrationProductRequestCount}
        experienceType={experienceType}
      />
      {isDesktop && !isPep && (
        <DynamicComponent pageType={pageType}>
          <Hydrator id="category-cards-desktop-bottom" className="sui-px-1">
            <ErrorBoundary>
              {products?.length < SERVER_SIDE_PRODUCT_POD_COUNT
                && (
                  <CategoryCardsDesktop
                    data={data}
                    error={error}
                    loading={loading}
                    storeId={storeId}
                    keyword={keyword}
                    navParam={nValue}
                    noMargin
                  />
                )}
            </ErrorBoundary>
          </Hydrator>
          <Hydrator id="sponsored-ingrid-bottom" className="sui-px-1">
            {isClientSearchModelResolved
              && isInGridBannerEnabled
              && isInGridBannerBottom && (
              <Col className="sui-pr-0">
                <ErrorBoundary name="sponsored-ingrid-nucleus">
                  <SponsoredInGridBanner
                    browserId={mcvisID || ''}
                    pageContext={{
                      label: 'browse-search',
                      data,
                      keyword,
                      isCategory,
                      isBrowse,
                      isSearch
                    }}
                  />
                </ErrorBoundary>
              </Col>
            )}
          </Hydrator>
        </DynamicComponent>
      )}
    </>
  );
});

DefaultGrid.displayName = 'DefaultGrid';
DefaultGrid.dataModel = extend({},
  DefaultResultsGrid,
  DiscoveryZonesWrapper,
  CategoryCardsDesktop,
  CategoryCardsMobile,
  CustomProductPodResponsiveB2B,
  CustomProductKeylessPodMobileB2B,
  CustomProductPianoPodMobileB2B
);

DefaultGrid.propTypes = {
  nValue: string,
  keyword: string,
  storeId: string.isRequired,
  itemId: string,
  productSSR: bool,
  searchSSR: bool,
  defaultVariables: shape({}).isRequired,
  additionalSearchParams: shape({}),
  pageType: string,
  handleProductClick: func,
  productPodHoverDelay: number,
  onCompare: func,
  onSelectedForCompare: func,
  selectedForCompare: arrayOf(string),
  isInStoreFilterON: bool,
  samplesLoading: bool,
  hasSameDayFilter: bool,
  impProductPodData: shape({}),
  ssrProductRequestCount: number,
  dynamicHydrationProductRequestCount: number,
  isInGridBannerBottom: bool,
  experienceType: string,
  searchModelData: proptypes.shape({}),
  searchModelLoading: bool,
  searchModelError: proptypes.shape({}),
  pageSize: number,
  inGridPlacements: arrayOf(shape({ componentName: string, row: number })),
};

DefaultGrid.defaultProps = {
  nValue: '',
  keyword: null,
  pageType: null,
  itemId: null,
  productSSR: false,
  searchSSR: true,
  additionalSearchParams: null,
  handleProductClick: undefined,
  productPodHoverDelay: 500,
  onCompare: null,
  onSelectedForCompare: null,
  selectedForCompare: null,
  isInStoreFilterON: false,
  samplesLoading: false,
  hasSameDayFilter: false,
  impProductPodData: null,
  ssrProductRequestCount: 12,
  dynamicHydrationProductRequestCount: 12,
  isInGridBannerBottom: false,
  experienceType: null,
  searchModelData: null,
  searchModelLoading: false,
  searchModelError: null,
  pageSize: null,
  inGridPlacements: [],
};

export { DefaultGrid };